import type { CheckResponse, HintResponse, Level, PublicPuzzle } from '../game/types.ts';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('offline', "Can't reach the server. Check your connection and try again.");
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(err?.code ?? 'error', err?.message ?? 'Something went wrong. Try again.');
  }
  return data as T;
}

/** Which hint to ask for: the easiest ("first") or one not shown before ("another"). */
export interface HintRequest {
  seen: string[];
  mode: 'first' | 'another';
  after?: string;
}

const serverApi = {
  nextPuzzle: (level: Level, exclude: string[]) => call<PublicPuzzle>('/puzzles/next', { level, exclude }),
  check: (id: string, board: string) => call<CheckResponse>(`/puzzles/${id}/check`, { board }),
  hint: (id: string, board: string, req: HintRequest) => call<HintResponse>(`/puzzles/${id}/hint`, { board, ...req }),
};

type Api = typeof serverApi;

/** Turns preview errors into the same ApiError the real API produces. */
function wrapPreview(impl: Pick<Api, 'check' | 'hint'> & { nextPuzzle: (level: Level) => Promise<PublicPuzzle> }): Api {
  const guard = <T>(p: Promise<T>) =>
    p.catch((e: { code?: string; message?: string }) => {
      throw new ApiError(e.code ?? 'error', e.message ?? 'Something went wrong. Try again.');
    });
  return {
    nextPuzzle: (level) => guard(impl.nextPuzzle(level)),
    check: (id, board) => guard(impl.check(id, board)),
    hint: (id, board, req) => guard(impl.hint(id, board, req)),
  };
}

/**
 * The real build always talks to the server. The preview build (VITE_PREVIEW=1)
 * swaps in an in-browser engine; Vite drops that branch from the real build.
 */
export const api: Api = import.meta.env.VITE_PREVIEW
  ? wrapPreview((await import('./demo.ts')).demoApi)
  : serverApi;
